import { createRoot } from 'react-dom/client'
import { useEffect, useRef, useState } from 'react'

import * as d3 from 'd3'
import { LngLat, Popup } from 'mapbox-gl'
import { tileToBBOX } from '@mapbox/tilebelt'

import { EPropertyType, IStacked } from '../../../../types-library/src'
import {
  useFilterStore,
  useMapStore,
  usePointStore
} from '../../../../state-library/src'
import { checkBoundsIntersects, getDesiredZoomLevel } from '../../utils'
import StackedList from './stakedList'

const localStorageKey = 'stacked_viewed'

const themeColors = {
  [EPropertyType.SALE]: {
    enabled: '#222',
    hover: '#97CC04',
    visited: '#ffffff'
  },
  [EPropertyType.RENT]: {
    enabled: '#222',
    hover: '#00C0C4',
    visited: '#ffffff'
  }
}

/**
 * This component is used to manage the points in the map
 * @returns {JSX.Element} - The JSX.Element
 */
const ListingsStackedsManager = ({
  goToDetail
}: {
  goToDetail: (property: any) => void
}): JSX.Element => {
  const requiredTiles: string[] = []

  // const { trackEvent } = useAmplitude()

  // const windowSize = useWindowSize()
  // const windowSizeRef = useRef<typeof windowSize>(windowSize)
  // windowSizeRef.current = windowSize

  // const {
  //   map,
  //   listingId,
  //   loadedStackeds,
  //   queryParams,
  //   clickedLayer,
  //   hoveredLayer,
  //   drawingTool,
  //   propertyType,
  //   listingCardHovered,
  //   listingsSvgContainer,
  //   selectedListingForMapPins,
  //   isEnablePropertiesPoints,

  //   setClickedLayer,
  //   setHoveredLayer,
  //   setListingCardHovered,
  //   setSelectedListingForMapPins,
  // } = useBoundStore((state) => state)

  const {
    map,
    drawingTool,
    hoveredLayer,
    clickedLayer,
    setClickedLayer,
    setHoveredLayer
  } = useMapStore()

  const {
    isEnablePropertiesPoints,
    loadedStackeds,
    tileQueryParams,
    pointsSvgContainer
  } = usePointStore()

  const { propertyType } = useFilterStore()

  const isEnablePropertiesPointsRef = useRef<typeof isEnablePropertiesPoints>(
    isEnablePropertiesPoints
  )
  isEnablePropertiesPointsRef.current = isEnablePropertiesPoints

  const tileQueryParamsRef = useRef<typeof tileQueryParams>(tileQueryParams)
  tileQueryParamsRef.current = tileQueryParams

  const [popup, setPopup] = useState<any>(null)
  const popupRef = useRef<typeof popup>(popup)
  popupRef.current = popup

  const [activePoint, setActivePoint] = useState<{
    listingId: string
    instance: any
  }>()
  const activePointRef = useRef<typeof activePoint>(activePoint)
  activePointRef.current = activePoint

  const [mobileStakedList, setMobileStakedList] = useState<
    IStacked | undefined
  >(undefined)

  // const listingCardHoveredRef =
  //   useRef<typeof listingCardHovered>(listingCardHovered)
  // listingCardHoveredRef.current = listingCardHovered

  const [listingHovered, setListingHovered] = useState<{
    listingId: string | undefined
    instance: any | undefined
  }>({
    listingId: undefined,
    instance: undefined
  })

  const drawingToolRef = useRef<typeof drawingTool>(drawingTool)
  drawingToolRef.current = drawingTool

  const propertyTypeRef = useRef<EPropertyType>(
    propertyType ?? EPropertyType.SALE
  )
  propertyTypeRef.current = propertyType ?? EPropertyType.SALE

  const pointsSvgContainerRef =
    useRef<typeof pointsSvgContainer>(pointsSvgContainer)
  pointsSvgContainerRef.current = pointsSvgContainer

  const hoveredLayerRef = useRef<typeof hoveredLayer>(hoveredLayer)
  hoveredLayerRef.current = hoveredLayer

  /**
   * This function is used to remove the popup
   * @param instance The instance of the popup
   * @returns {void}
   */
  const removePopup = (): void => {
    if (!popupRef?.current) {
      return
    }
    popupRef.current.remove()
    setPopup(null)
    setMobileStakedList(undefined)
  }

  /**
   * This function is used to create the HTML of the popup
   * @param id The id of the listing
   * @returns {HTMLDivElement}
   */
  const createPopupHTML = (params: { stacked: IStacked }): HTMLDivElement => {
    const { stacked } = params

    const divContainer = document.createElement('div')

    const root = createRoot(divContainer)
    root.render(
      <StackedList
        getRef={setPopup}
        stacked={stacked}
        onClose={removePopup}
        onSelect={goToDetail}
        // trackEvent={trackEvent}
      />
    )

    return divContainer
  }

  /**
   * This function is used to create the listing popup
   * @param listing The listing data
   * @returns {void}
   */
  const createStackedPopup = (params: { stacked: IStacked }): void => {
    if (!map) {
      return
    }

    const { stacked } = params

    const popupHTML = createPopupHTML({ stacked })

    const [longitude, latitude] = stacked?.geometry?.coordinates ?? []

    new Popup({
      offset: [-90, -30],
      closeButton: false,
      closeOnClick: false
    })
      ?.setLngLat({
        lat: latitude,
        lng: longitude
      })
      ?.setDOMContent(popupHTML)
      ?.addTo(map)
  }

  /**
   * This function is used to center the map to the listing
   * @returns {void}
   */
  const toCenter = (params: {
    lat: number
    lng: number
    withOffset: boolean
  }): void => {
    const { lat, lng, withOffset } = params
    map?.flyTo({
      center: [lng, lat],
      offset: [0, withOffset ? 135 : 0],
      speed: 0.7,
      duration: 300
    })
  }

  /**
   * @name registerListingInLocalStorage
   * @description This function is used to register the listing in the local storage
   * @returns {void}
   */
  const registerStackedInLocalStorage = (params: {
    listingId: string
  }): void => {
    const { listingId } = params

    const isReviewedListing = verifyIfStackedIsReviewedListing({
      listingsId: [listingId]
    })

    if (isReviewedListing) {
      return
    }

    const listings = JSON.parse(
      localStorage.getItem(localStorageKey) || '[]'
    ) as string[]

    listings?.push(listingId)

    localStorage.setItem(localStorageKey, JSON.stringify(listings))
  }

  /**
   * @name verifyIfListingIsReviewedListing
   * @description This function is used to verify if the listing is reviewed
   * @returns {void}
   */
  const verifyIfStackedIsReviewedListing = (params: {
    listingsId: string[]
  }): boolean => {
    const { listingsId } = params
    const listings = JSON.parse(
      localStorage.getItem(localStorageKey) || '[]'
    ) as string[]

    let isInLocalStorage = false
    for (const id of listingsId) {
      if (listings.includes(id)) {
        isInLocalStorage = true
        break
      }
    }

    return isInLocalStorage
  }

  /**
   * This function is used to reset all the active points
   * @returns {void}
   */
  const resetAllActivePoints = (): void => {
    const points = d3?.selectAll('g.listingStacked')

    points?.select('rect')?.style('fill', (dValue: IStacked | any) => {
      const properties = dValue?.properties

      if (!properties) {
        return themeColors[propertyTypeRef.current]?.enabled
      }

      const isReviewedListing = verifyIfStackedIsReviewedListing({
        listingsId:
          properties?.stacked?.map((listing: IStacked) => listing.id) || []
      })

      return isReviewedListing
        ? themeColors[propertyTypeRef?.current]?.visited
        : themeColors[propertyTypeRef?.current]?.enabled
    })

    points.selectAll('text')?.style('fill', (dValue: IStacked | any) => {
      const properties = dValue?.properties

      if (!properties) {
        return '#ffffff'
      }

      const isReviewedListing = verifyIfStackedIsReviewedListing({
        listingsId:
          properties?.stacked?.map((listing: IStacked) => listing?.id) || []
      })

      return isReviewedListing ? '#898CA8' : '#ffffff'
    })

    points.selectAll('svg').attr('fill', (dValue: IStacked | any) => {
      const properties = dValue?.properties

      if (!properties) {
        return '#ffffff'
      }

      const isReviewedListing = verifyIfStackedIsReviewedListing({
        listingsId:
          properties.stacked.map((listing: IStacked) => listing.id) || []
      })

      return isReviewedListing ? '#898CA8' : '#ffffff'
    })
  }

  function project(dValue: number[]) {
    return map?.project(new LngLat(dValue[0], dValue[1]))
  }

  const createStackedsPoints = (params: {
    key: string
    spatialIndices: {
      xIndex: number
      yIndex: number
      zIndex: number
    }
    features: IStacked[]
  }): void => {
    const {
      spatialIndices: { xIndex, yIndex, zIndex },
      features
    } = params

    const { minZoom, maxZoom } = getDesiredZoomLevel({
      zoom: zIndex
    })

    const spatialIndex = `${xIndex}-${yIndex}-${zIndex}`

    if (requiredTiles?.includes(spatialIndex) || !pointsSvgContainer) {
      return
    }

    requiredTiles?.push(spatialIndex)

    let group: any

    const createGroup = (): void => {
      group = pointsSvgContainer
        .append('g')
        .attr('id', `group-${spatialIndex}-listingStacked`)

      const points = group
        ?.selectAll('g.listingStacked')
        ?.data(features)
        ?.enter()
        ?.append('g')
        ?.attr('class', 'listingStacked')
        ?.on(
          'click',
          (
            dEvent: {
              currentTarget: { parentNode: any }
              stopPropagation: () => void
            },
            dData: IStacked
          ) => {
            dEvent?.stopPropagation()

            if (
              drawingToolRef?.current?.isActive &&
              drawingToolRef?.current?.status !== 'processed'
            ) {
              return
            }

            setClickedLayer({
              layerId: 'listingStacked',
              properties: undefined
            })

            removePopup()
            resetAllActivePoints()

            const point = d3.select(dEvent?.currentTarget as any)

            const rect = point.select('rect')

            illuminatePoint({
              pointInstance: point
            })

            const { properties } = dData

            const firstListing = properties.stacked[0]

            setActivePoint({
              listingId: firstListing.id,
              instance: rect
            })

            // const phoneScreen = windowSizeRef.current.width < 768

            // if (phoneScreen) {
            //   setMobileStakedList(dData)
            // } else {
            //   createStackedPopup({
            //     stacked: dData,
            //   })
            // }
            createStackedPopup({
              stacked: dData
            })

            const [longitude, latitude] = dData?.geometry?.coordinates

            toCenter({
              lat: latitude ?? 0,
              lng: longitude ?? 0,
              // withOffset: !phoneScreen,
              withOffset: true
            })

            registerStackedInLocalStorage({
              listingId: firstListing.id
            })
          }
        )
        ?.on('mouseover', (dEvent: any, dData: IStacked) => {
          if (
            drawingToolRef?.current?.isActive &&
            drawingToolRef?.current?.status !== 'processed'
          ) {
            return
          }

          pointsSvgContainer.style('cursor', 'pointer')

          // setListingCardHovered(undefined)
          setHoveredLayer({
            layerId: 'listingStacked',
            properties: undefined
          })

          const point = d3?.select(dEvent.currentTarget)

          const { properties } = dData

          const firstListing = properties?.stacked[0]

          setListingHovered({
            listingId: firstListing.id,
            instance: point
          })
        })
        ?.on('mouseout', () => {
          setListingHovered({
            listingId: undefined,
            instance: undefined
          })
          setHoveredLayer(undefined)

          if (
            drawingToolRef?.current?.isActive &&
            drawingToolRef?.current?.status !== 'processed'
          ) {
            return
          }

          pointsSvgContainer.style('cursor', 'grab')
        })

      points
        ?.append('rect')
        ?.attr('width', (dValue: { properties: { stackedCount: any } }) => {
          const stackedCount = dValue?.properties?.stackedCount
          const stackedCountLength = stackedCount.toString().length
          const variant = 3.5 * stackedCountLength
          if (stackedCountLength === 1) {
            return 100
          }
          return 100 + variant
        })
        ?.attr('x', -50)
        ?.attr('height', 27)
        ?.style('stroke', '#D1D1D1')
        ?.style('stroke-width', 1)
        ?.attr('rx', 14)
        ?.attr('ry', 14)
        ?.style('fill', '#181A3D')
        ?.style('fill', (dValue: IStacked) => {
          const { properties } = dValue

          // const isHovered = properties.stacked.find(
          //   (listing) => listing.id === listingCardHoveredRef?.current?.id
          // )?.id

          // if (isHovered) {
          //   return themeColors[propertyTypeRef.current]?.hover
          // }

          const findedListing = properties.stacked.find(
            (listing) => listing.id === activePointRef?.current?.listingId
          )

          if (!findedListing) {
            const isReviewedListing = verifyIfStackedIsReviewedListing({
              listingsId:
                properties?.stacked.map((listing) => listing?.id) || []
            })

            if (isReviewedListing) {
              return themeColors[propertyTypeRef.current]?.visited
            }

            return themeColors[propertyTypeRef.current]?.enabled
          }

          return themeColors[propertyTypeRef.current]?.hover
        })

      const iconTextGroup = points.append('g')

      iconTextGroup
        ?.append('svg')
        ?.attr('xmlns', 'http://www.w3.org/2000/svg')
        ?.attr('height', '14')
        ?.attr('x', -50)
        ?.attr('y', 0.5)
        ?.attr('viewBox', '0 -960 960 960')
        ?.attr('width', '14')
        ?.attr('fill', (dValue: IStacked) => {
          const { properties } = dValue

          // const isHovered = properties.stacked.find(
          //   (listing: any) => listing.id === listingCardHoveredRef?.current?.id
          // )?.id

          // if (isHovered) {
          //   return '#ffffff' // Color de relleno cuando está siendo 'hovered'
          // }

          const findedListing = properties?.stacked?.find(
            (listing: any) => listing.id === activePointRef?.current?.listingId
          )

          if (!findedListing) {
            const isReviewedListing = verifyIfStackedIsReviewedListing({
              listingsId:
                properties?.stacked?.map((listing: any) => listing.id) || []
            })

            if (isReviewedListing) {
              return '#898CA8' // Color de relleno cuando es una lista revisada
            }
          }

          return '#ffffff' // Color de relleno predeterminado
        })
        ?.append('path')
        ?.attr(
          'd',
          'M80-120v-720h400v160h400v560H80Zm80-80h80v-80h-80v80Zm0-160h80v-80h-80v80Zm0-160h80v-80h-80v80Zm0-160h80v-80h-80v80Zm160 480h80v-80h-80v80Zm0-160h80v-80h-80v80Zm0-160h80v-80h-80v80Zm0-160h80v-80h-80v80Zm160 480h320v-400H480v80h80v80h-80v80h80v80h-80v80Zm160-240v-80h80v80h-80Zm0 160v-80h80v80h-80Z'
        )

      iconTextGroup
        ?.append('text')
        ?.attr('x', -31)
        ?.style('font-size', '12px')
        ?.attr('font-family', 'Ret Hat Display')
        ?.attr('dy', '12.5px')
        ?.attr('font-weight', 700)
        ?.text(
          (dValue: { properties: { stackedCount: any } }) =>
            `${dValue.properties.stackedCount} Anuncios`
        )
        ?.style('fill', (dValue: IStacked) => {
          const { properties } = dValue

          // const isHovered = properties.stacked.find(
          //   (listing) => listing.id === listingCardHoveredRef?.current?.id
          // )?.id

          // if (isHovered) {
          //   return '#ffffff'
          // }

          const findedListing = properties?.stacked?.find(
            (listing) => listing.id === activePointRef?.current?.listingId
          )

          if (!findedListing) {
            const isReviewedListing = verifyIfStackedIsReviewedListing({
              listingsId:
                properties?.stacked?.map((listing) => listing?.id) || []
            })

            if (isReviewedListing) {
              return '#898CA8'
            }
          }

          return '#ffffff'
        })

      iconTextGroup?.attr('transform', `translate(${8}, 5)`)

      render()
    }

    createGroup()

    function render() {
      if (
        params?.key !== tileQueryParamsRef?.current ||
        !isEnablePropertiesPointsRef?.current
      ) {
        group?.remove()
        group = null

        if (hoveredLayerRef?.current?.layerId === 'listingStacked') {
          setHoveredLayer(undefined)
          pointsSvgContainerRef?.current?.style('cursor', 'grab')
        }

        map?.off('move', render)
        map?.off('moveend', render)
        return
      }

      const mapBounds: any = map?.getBounds()
      const tileBounds: any = tileToBBOX([xIndex, yIndex, zIndex])

      const intersects = checkBoundsIntersects({
        mapBounds,
        tileBounds
      })

      const currentZoom = map?.getZoom() || 0
      const isZoomed = currentZoom >= minZoom && currentZoom <= maxZoom

      if (intersects && isZoomed) {
        if (!group) {
          createGroup()
        }
        group
          ?.selectAll('g.listingStacked')
          ?.attr(
            'transform',
            (dValue: { geometry: { coordinates: number[] } }) => {
              const coordinates = project(dValue.geometry.coordinates)
              return `translate(${coordinates?.x},${coordinates?.y})`
            }
          )
      } else if (group) {
        group?.remove()
        group = null

        if (hoveredLayerRef?.current?.layerId === 'listingStacked') {
          setHoveredLayer(undefined)
          pointsSvgContainerRef?.current?.style('cursor', 'grab')
        }
      }
    }

    map?.on('move', render)
    map?.on('moveend', render)
  }

  const illuminatePoint = (params: { pointInstance: any }): void => {
    const { pointInstance } = params

    if (!pointInstance?.size()) {
      return
    }

    pointInstance?.raise()

    const rect = pointInstance?.select('rect')
    rect.style('fill', () => {
      return themeColors[propertyTypeRef.current]?.hover
    })

    const text = pointInstance?.selectAll('text')
    const svg = pointInstance?.selectAll('svg')
    svg?.attr('fill', '#ffffff')
    text?.style('fill', '#ffffff')
  }

  const turnOffAllPoints = (): void => {
    const allPoints = pointsSvgContainer.selectAll('g.listingStacked')

    const rect = allPoints.select('rect')
    rect.style('fill', (dValue: IStacked) => {
      const { properties } = dValue

      const findedListing = properties.stacked.find(
        (listing) => listing.id === activePointRef?.current?.listingId
      )

      if (!findedListing) {
        const isReviewedListing = verifyIfStackedIsReviewedListing({
          listingsId: properties.stacked.map((listing) => listing.id) || []
        })

        if (isReviewedListing) {
          return themeColors[propertyTypeRef.current]?.visited
        }

        return themeColors[propertyTypeRef.current]?.enabled
      }

      return themeColors[propertyTypeRef.current]?.hover
    })

    const text = allPoints.selectAll('text')
    text?.style('fill', (dValue: IStacked) => {
      const { properties } = dValue

      const findedListing = properties.stacked.find(
        (listing) => listing.id === activePointRef?.current?.listingId
      )

      if (!findedListing) {
        const isReviewedListing = verifyIfStackedIsReviewedListing({
          listingsId: properties?.stacked?.map((listing) => listing?.id) || []
        })

        if (isReviewedListing) {
          return '#898CA8'
        }
      }

      return '#ffffff'
    })

    const svg = allPoints?.selectAll('svg')
    svg?.attr('fill', (dValue: IStacked) => {
      const { properties } = dValue

      const findedListing = properties.stacked.find(
        (listing) => listing.id === activePointRef?.current?.listingId
      )

      if (!findedListing) {
        const isReviewedListing = verifyIfStackedIsReviewedListing({
          listingsId: properties?.stacked?.map((listing) => listing?.id) || []
        })

        if (isReviewedListing) {
          return '#898CA8'
        }
      }

      return '#ffffff'
    })
  }

  const changePointStyles = (): void => {
    if (!pointsSvgContainer) {
      return
    }

    const allPoints = pointsSvgContainer.selectAll('g.listingStacked')

    const rect = allPoints?.select('rect')
    rect?.style('fill', (dValue: IStacked) => {
      const { properties } = dValue

      const isHovered = properties?.stacked?.find(
        (listing) => listing.id === listingHovered?.listingId
      )?.id

      if (isHovered) {
        return themeColors[propertyTypeRef.current]?.hover
      }

      const findedListing = properties?.stacked?.find(
        (listing) => listing.id === activePointRef?.current?.listingId
      )

      if (!findedListing) {
        const isReviewedListing = verifyIfStackedIsReviewedListing({
          listingsId: properties?.stacked?.map((listing) => listing?.id) || []
        })

        if (isReviewedListing) {
          return themeColors[propertyTypeRef.current]?.visited
        }

        return themeColors[propertyTypeRef.current]?.enabled
      }

      return themeColors[propertyTypeRef.current]?.hover
    })

    const text = allPoints?.selectAll('text')
    text?.style('fill', (dValue: IStacked) => {
      const { properties } = dValue

      const isHovered = properties?.stacked?.find(
        (listing) => listing.id === listingHovered?.listingId
      )?.id

      if (isHovered) {
        return '#ffffff'
      }

      const findedListing = properties?.stacked?.find(
        (listing) => listing?.id === activePointRef?.current?.listingId
      )

      if (!findedListing) {
        const isReviewedListing = verifyIfStackedIsReviewedListing({
          listingsId: properties?.stacked?.map((listing) => listing.id) || []
        })

        if (isReviewedListing) {
          return '#898CA8'
        }
      }

      return '#ffffff'
    })

    const svg = allPoints.selectAll('svg')
    svg?.attr('fill', (dValue: IStacked) => {
      const { properties } = dValue

      const isHovered = properties?.stacked?.find(
        (listing) => listing?.id === listingHovered?.listingId
      )?.id

      if (isHovered) {
        return '#ffffff'
      }

      const findedListing = properties?.stacked?.find(
        (listing) => listing?.id === activePointRef?.current?.listingId
      )

      if (!findedListing) {
        const isReviewedListing = verifyIfStackedIsReviewedListing({
          listingsId: properties?.stacked?.map((listing) => listing?.id) || []
        })

        if (isReviewedListing) {
          return '#898CA8'
        }
      }

      return '#ffffff'
    })

    if (listingHovered?.listingId) {
      listingHovered?.instance?.raise()
    }
  }

  /**
   * This effect is used to toggle the active point
   */
  useEffect(() => {
    const toggleActivePoint = (dEvent: any) => {
      const modal = document.getElementById('property-sheet-modal')
      const isInsideModal = modal && modal.contains(dEvent?.target)
      if (isInsideModal) {
        return
      }

      const isInsidePopup =
        popupRef?.current && popupRef?.current?.contains(dEvent?.target)
      if (isInsidePopup) {
        return
      }

      if (activePoint) {
        resetAllActivePoints()
        setActivePoint(undefined)
      }

      if (popupRef && !popupRef?.current?.contains(dEvent?.target)) {
        removePopup()
      }
    }
    document.addEventListener('click', toggleActivePoint)

    return () => {
      document.removeEventListener('click', toggleActivePoint)
    }
    // }, [activePoint, listingId, popupRef, popup])
  }, [activePoint, popupRef, popup])

  /**
   * This effect is used to active point from propertySheetModal
   */
  // useEffect(() => {
  //   if (selectedListingForMapPins) {
  //     const points = d3.selectAll('g.listingStacked')
  //     points.select('rect').style('fill', (dValue: ListingStacked | any) => {
  //       const { properties } = dValue

  //       const findedListing = properties.stacked.find(
  //         (listing: any) => listing.id === selectedListingForMapPins
  //       )

  //       if (!findedListing) {
  //         const isReviewedListing = verifyIfStackedIsReviewedListing({
  //           listingsId:
  //             properties.stacked.map((listing: any) => listing.id) || [],
  //         })

  //         if (isReviewedListing) {
  //           return themeColors[propertyTypeRef.current]?.visited
  //         }

  //         return themeColors[propertyTypeRef.current]?.enabled
  //       }

  //       return themeColors[propertyTypeRef.current]?.hover
  //     })

  //     points.selectAll('text').style('fill', (dValue: ListingStacked | any) => {
  //       const { properties } = dValue

  //       const findedListing = properties.stacked.find(
  //         (listing: any) => listing.id === selectedListingForMapPins
  //       )

  //       if (!findedListing) {
  //         const isReviewedListing = verifyIfStackedIsReviewedListing({
  //           listingsId:
  //             properties.stacked.map((listing: any) => listing.id) || [],
  //         })

  //         if (isReviewedListing) {
  //           return '#898CA8'
  //         }
  //       }

  //       return '#ffffff'
  //     })

  //     setActivePoint({
  //       listingId: selectedListingForMapPins,
  //       instance: null,
  //     })

  //     registerStackedInLocalStorage({
  //       listingId: selectedListingForMapPins,
  //     })
  //     setSelectedListingForMapPins(undefined)
  //   }
  // }, [selectedListingForMapPins])

  useEffect(() => {
    if (clickedLayer?.layerId === 'listingPoint') {
      setActivePoint(undefined)
      removePopup()
      resetAllActivePoints()
    }
  }, [clickedLayer])

  useEffect(() => {
    if (loadedStackeds.length && pointsSvgContainer) {
      const lastGroup = loadedStackeds[loadedStackeds.length - 1]
      if (!lastGroup?.features?.length) {
        return
      }
      createStackedsPoints(lastGroup)
    }
  }, [loadedStackeds, pointsSvgContainer])

  // useEffect(() => {
  //   if (!listingCardHovered?.id) {
  //     return
  //   }

  //   const findPoint = (id: string) => {
  //     const allPoints = listingsSvgContainer.selectAll('g.listingStacked')

  //     const find = allPoints.filter((dValue: any) => {
  //       const { properties } = dValue
  //       return properties.stacked.find(
  //         (listing: { id: string | undefined }) => listing.id === id
  //       )
  //     })

  //     if (!find?.size()) {
  //       return undefined
  //     }

  //     return find
  //   }

  //   const find = findPoint(listingCardHovered.id)

  //   if (!find) {
  //     turnOffAllPoints()
  //     return
  //   }

  //   setListingHovered({
  //     listingId: listingCardHovered.id,
  //     instance: find,
  //   })
  // }, [listingCardHovered])

  useEffect(() => {
    changePointStyles()
  }, [listingHovered])

  useEffect(() => {
    if (hoveredLayer?.layerId !== 'listingStacked') {
      setListingHovered({
        listingId: undefined,
        instance: undefined
      })
    }
  }, [hoveredLayer])

  return (
    <>
      {/* {mobileStakedList &&
        createPortal(
          <div className="fixed top-[64px] z-[5] h-[calc(100%-64px)] w-full">
            <LazyMotion features={domAnimation}>
              <m.div
                onClick={(event) => event.stopPropagation()}
                initial={{
                  y: '100vh',
                  opacity: 0,
                }}
                animate={{
                  y: 0,
                  opacity: 1,
                }}
                exit={{
                  y: '100vh',
                  opacity: 0,
                }}
                transition={{
                  type: 'just',
                }}
                className="h-full w-full"
              >
                <StackedList
                  getRef={setPopup}
                  stacked={mobileStakedList}
                  onClose={() => {
                    setMobileStakedList(undefined)
                  }}
                  onSelect={() => {
                    setMobileStakedList(undefined)
                  }}
                  trackEvent={trackEvent}
                />
              </m.div>
            </LazyMotion>
          </div>,
          document.body
        )} */}
    </>
  )
}

export default ListingsStackedsManager
